import axios from "axios";

// Where the frontend talks to the backend.
//
// Historical: we used to build the URL from REACT_APP_BACKEND_URL at build
// time. That baked in a single hostname at compile time, so the bundle shipped
// to https://www.thatgantt.com kept calling https://milestone-chart-1.emergent.host
// as its API origin — i.e. cross-origin, which forced CORS + credentials, which
// the Emergent ingress can't safely serve with a wildcard origin, which broke
// auth for every real user.
//
// Fix: every Emergent-hosted origin (preview pod, emergent.host, custom domain)
// routes `/api/*` to the backend same-origin via the ingress, so we just use the
// current page's origin at runtime. This makes auth cookies same-origin, drops
// CORS out of the request entirely, and means a single bundle works everywhere
// — preview, production, and any future custom domain — with no rebuild needed.
//
// Fallback to REACT_APP_BACKEND_URL if window is unavailable (SSR/test).
// Hardening: even though Cloudflare 301-redirects http→https and HSTS forces
// https for 2 years after the first visit, we also upgrade any http:// origin
// to https:// here so a stale bookmark, a misconfigured reverse proxy, or a
// test harness can't accidentally downgrade the connection to plaintext.
let runtimeOrigin = typeof window !== "undefined" && window.location?.origin
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL || "";
if (runtimeOrigin.startsWith("http://") && !runtimeOrigin.startsWith("http://localhost")) {
  runtimeOrigin = runtimeOrigin.replace(/^http:\/\//, "https://");
}
export const API = `${runtimeOrigin.replace(/\/$/, "")}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// ---------- CSRF (double-submit cookie) ----------
//
// The backend issues a non-HttpOnly `csrf_token` cookie on /api/auth/* success
// and on /api/auth/csrf. We read it via `document.cookie` and echo it in the
// `X-CSRF-Token` header on every mutating request. The backend middleware
// compares cookie vs header and rejects with 403 on mismatch.
function readCookie(name) {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  const found = document.cookie.split("; ").find((c) => c.startsWith(prefix));
  return found ? decodeURIComponent(found.slice(prefix.length)) : null;
}

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (method !== "get" && method !== "head" && method !== "options") {
    const token = readCookie("csrf_token");
    if (token) {
      config.headers = config.headers || {};
      config.headers["X-CSRF-Token"] = token;
    }
  }
  return config;
});

// Fire-and-forget on module load: makes sure the cookie is set on cold page
// loads where the user has a valid session but no CSRF cookie (e.g. an old
// tab pre-dating the security upgrade).
export async function ensureCsrf() {
  if (readCookie("csrf_token")) return;
  try {
    await api.get("/auth/csrf");
  } catch (_) {
    /* swallow — login/register will set it later */
  }
}
if (typeof window !== "undefined") {
  ensureCsrf();
}

export async function createProject(payload) {
  const { data } = await api.post("/projects", payload);
  return data;
}

export async function fetchProject(projectId, email) {
  const params = email ? { email } : {};
  const { data } = await api.get(`/projects/${projectId}`, { params });
  return data;
}

export async function accessCheck(projectId, email) {
  const { data } = await api.post(`/projects/${projectId}/access-check`, { email });
  return data;
}

export async function updateProject(projectId, payload, email) {
  const params = email ? { email } : {};
  const { data } = await api.put(`/projects/${projectId}`, payload, { params });
  return data;
}

export async function deleteProjectApi(projectId) {
  const { data } = await api.delete(`/projects/${projectId}`);
  return data;
}

export async function duplicateProject(projectId) {
  const { data } = await api.post(`/projects/${projectId}/duplicate`);
  return data;
}

export async function fetchProjectMembers(projectId) {
  const { data } = await api.get(`/projects/${projectId}/members`);
  return data.members || [];
}

export async function fetchProjectActivity(projectId, email, limit = 50) {
  const params = { limit };
  if (email) params.email = email;
  const { data } = await api.get(`/projects/${projectId}/activity`, { params });
  return data.activity || [];
}

export async function fetchMyActivity(limit = 30) {
  const { data } = await api.get(`/my/activity`, { params: { limit } });
  return data.activity || [];
}

export async function updateProfile(payload) {
  const { data } = await api.put(`/auth/profile`, payload);
  return data;
}

// -------- Folders --------
export async function fetchFolders() {
  const { data } = await api.get(`/folders`);
  return data.folders || [];
}

export async function createFolder(payload) {
  const { data } = await api.post(`/folders`, payload);
  return data;
}

export async function updateFolder(folderId, payload) {
  const { data } = await api.put(`/folders/${folderId}`, payload);
  return data;
}

export async function deleteFolder(folderId) {
  const { data } = await api.delete(`/folders/${folderId}`);
  return data;
}

export async function moveProjectToFolder(folderId, projectId) {
  const { data } = await api.post(`/folders/${folderId}/projects/${projectId}`);
  return data;
}

export async function removeProjectFromFolder(folderId, projectId) {
  const { data } = await api.delete(`/folders/${folderId}/projects/${projectId}`);
  return data;
}

export async function updateDistribution(projectId, entries) {
  const { data } = await api.put(`/projects/${projectId}/distribution`, { entries });
  return data;
}

export async function createTask(projectId, payload, email) {
  const params = email ? { email } : {};
  const { data } = await api.post(`/projects/${projectId}/tasks`, payload, { params });
  return data;
}

export async function updateTask(taskId, payload, email) {
  const params = email ? { email } : {};
  const { data } = await api.put(`/tasks/${taskId}`, payload, { params });
  return data;
}

export async function deleteTask(taskId, email) {
  const params = email ? { email } : {};
  const { data } = await api.delete(`/tasks/${taskId}`, { params });
  return data;
}

export async function reorderTasks(projectId, items, email) {
  const params = email ? { email } : {};
  const { data } = await api.post(`/projects/${projectId}/tasks/reorder`, items, { params });
  return data;
}

export async function addTaskEvent(taskId, payload, email) {
  const params = email ? { email } : {};
  const { data } = await api.post(`/tasks/${taskId}/events`, payload, { params });
  return data;
}

export async function deleteTaskEvent(taskId, eventId, email) {
  const params = email ? { email } : {};
  const { data } = await api.delete(`/tasks/${taskId}/events/${eventId}`, { params });
  return data;
}

export async function listVersions(projectId, email) {
  const params = email ? { email } : {};
  const { data } = await api.get(`/projects/${projectId}/versions`, { params });
  return data.versions;
}

export async function createVersion(projectId, note, email) {
  const params = email ? { email } : {};
  const { data } = await api.post(`/projects/${projectId}/versions`, { note }, { params });
  return data;
}

export async function restoreVersion(projectId, versionId, email) {
  const params = email ? { email } : {};
  const { data } = await api.post(`/projects/${projectId}/versions/${versionId}/restore`, {}, { params });
  return data;
}

export async function fetchMyProjects() {
  const { data } = await api.get("/my/projects");
  return data;
}

export async function importFile(projectId, file, replace) {
  const form = new FormData();
  form.append("file", file);
  form.append("replace", String(!!replace));
  const { data } = await api.post(`/projects/${projectId}/import`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

// -------- Phase C: Support Tickets --------
export async function submitSupportTicket(payload) {
  const { data } = await api.post(`/support/tickets`, payload);
  return data;
}

// -------- Phase C+ : Auth flows --------
export async function forgotPassword(email) {
  const { data } = await api.post(`/auth/forgot-password`, { email });
  return data;
}

export async function resetPassword(token, newPassword) {
  const { data } = await api.post(`/auth/reset-password`, {
    token,
    new_password: newPassword,
  });
  return data;
}

export async function deactivateAccount(password) {
  const { data } = await api.post(`/auth/deactivate`, { password });
  return data;
}

// -------- Phase C: Transfer Ownership --------
export async function requestProjectTransfer(projectId, newOwnerEmail, note) {
  const { data } = await api.post(`/projects/${projectId}/transfer`, {
    new_owner_email: newOwnerEmail,
    note: note || "",
  });
  return data;
}

export async function getPendingTransfer(projectId) {
  const { data } = await api.get(`/projects/${projectId}/transfer/pending`);
  return data.pending;
}

export async function cancelPendingTransfer(projectId) {
  const { data } = await api.delete(`/projects/${projectId}/transfer/pending`);
  return data;
}

export async function acceptProjectTransfer(token) {
  const { data } = await api.post(`/projects/transfer/accept`, null, { params: { token } });
  return data;
}


// ---------------- Billing (Phase D: Stripe Subscriptions) ----------------

export async function createCheckoutSession({ tier, cadence, origin_url }) {
  const { data } = await api.post("/billing/checkout", { tier, cadence, origin_url });
  return data; // { url, session_id }
}

export async function getBillingStatus(sessionId) {
  const { data } = await api.get(`/billing/status/${sessionId}`);
  return data;
}
